<template>
  <div class="tabla-componentes">
    <table class="tabla-modern">
      <thead>
        <tr>
          <th>Ordinal</th>
          <th>Nombre</th>
          <th>Acciones</th>
        </tr>
      </thead>
      <tbody>
        <FilaComponente
          v-for="(comp, index) in sortedComponentes"
          :key="comp.id_componente"
          :comp="comp"
          :nivel="0"
          :ultimoNivel="ultimoNivel"
          :posicion="index + 1"
          :totalHermanos="sortedComponentes.length"
          v-model:componenteSeleccionado="componenteSeleccionado"
          :expandido="expandido"
          :toggleExpandido="toggleExpandido"
          :esIndicadores="props.esIndicadores"
          :esIndicadorFn="props.esIndicadorFn"
          :puedeAcceder="props.puedeAcceder"
          :instrumentosPorIndicadorFn="instrumentosPorIndicadorFn"
          :mostrarBotonCalificar="props.mostrarBotonCalificar"
          :mostrarAccionesComponente="props.mostrarAccionesComponente"
          :mostrarAccionesIndicador="props.mostrarAccionesIndicador"
          :ocultarFilaComponente="props.ocultarFilaComponente"
          :componenteEditandoId="props.componenteEditandoId"
          :niveles="props.niveles"
          :obtenerNumeracion="props.obtenerNumeracion"
          :loading="props.loading"
          @modificar="onModificar"
          @eliminar="$emit('eliminar', $event)"
          @agregarHijo="onAgregarHijo"
          @agregarInstrumento="onAgregarInstrumento"
          @vincularFormulario="emit('vincularFormulario', $event)"
          @modificarIndicador="onModificarIndicador"
          @eliminarIndicador="onEliminarIndicador"
          @acceder="onAcceder"
          @calificarInstrumento="$emit('calificarInstrumento', $event)"
          @calificarIndicador="$emit('calificarIndicador', $event)"
          @modificarInstrumento="$emit('modificarInstrumento', $event)"
          @eliminarInstrumento="$emit('eliminarInstrumento', $event)"
          @reordenar="$emit('reordenar', $event)"
        />
      </tbody>
    </table>
  </div>
</template>

<script lang="ts" setup>
/**
 * Edición en lote de los componentes del árbol.
 *
 * Componente de presentación. Recibe sus datos por props o del store del módulo.
 */

import type Componente from '@/interfaces/Componente'
import type Indicador from '@/interfaces/Indicador'
import { computed, provide, reactive, ref, watch } from 'vue'
import { useComponentes } from '@/store/GestionComponentes'
import FilaComponente from './FilaComponente.vue'
import { useGestionVersiones } from '@/store/GestionVersiones'
import { useModelo } from '@/store/GestionModelo'
import type Instrumento from '@/interfaces/Instrumetos'

const versionesStore = useGestionVersiones()
const modeloStore = useModelo()

const ultimoNivel = computed(() => {
  // Usar el prop niveles si está disponible, sino calcular desde el modelo
  if (props.niveles && props.niveles.length > 0) {
    return props.niveles.length
  }
  const modelo = modeloStore.modeloActual
  if (!modelo) return 1
  return (modelo.niveles?.length ?? 0) + 1
})

const props = withDefaults(defineProps<{
  componentes: Componente[]
  idComponenteRaiz: number | null
  componenteEditandoId?: number | null
  esIndicadores?: boolean
  esIndicadorFn?: (c: Componente) => boolean
  puedeAcceder?: boolean
  indicador?: Indicador
  instrumentosPorIndicadorFn?: (id_indicador: number) => Instrumento[]
  mostrarBotonCalificar?: boolean
  mostrarAccionesComponente?: boolean
  mostrarAccionesIndicador?: boolean
  ocultarFilaComponente?: boolean
  niveles?: string[]
  obtenerNumeracion?: (comp: Componente, nivel: number) => string
  loading?: boolean
}>(), {
  mostrarAccionesComponente: true,
  mostrarAccionesIndicador: true,
  ocultarFilaComponente: false,
  loading: false,
})

const emit = defineEmits<{
  (e: 'modificar', comp: Componente): void
  (e: 'eliminar', comp: Componente): void
  (e: 'agregarHijo', payload: { padre: Componente; nivel: number }): void
  (e: 'agregarInstrumento', indicador?: Indicador): void
  (e: 'vincularFormulario', indicador: Indicador): void
  (e: 'modificarIndicador', indicador: Indicador): void
  (e: 'eliminarIndicador', indicador: Indicador): void
  (e: 'acceder', comp: Componente): void
  (e: 'calificarInstrumento', payload: { instrumento?: Instrumento, indicador?: Indicador }): void
  (e: 'calificarIndicador', payload: { indicador: Indicador }): void
  (e: 'modificarInstrumento', instrumento: Instrumento): void
  (e: 'eliminarInstrumento', payload: { instrumento: Instrumento; indicador: Indicador }): void
  (e: 'reordenar', payload: { dragged: Componente; target: Componente; side: 'before' | 'after' }): void
}>()

const store = useComponentes()

const dragState = reactive<{
  dragging: Componente | null
  dropTargetId: number | null
  dropSide: 'before' | 'after'
}>({ dragging: null, dropTargetId: null, dropSide: 'after' })
provide('dragState', dragState)

const sortedComponentes = computed(() =>
  [...props.componentes].sort((a, b) =>
    (a.ordinal ?? '').localeCompare(b.ordinal ?? '', undefined, { numeric: true })
  )
)

const componenteSeleccionado = ref<Componente | null>(null)
const expandido = ref<Set<number>>(new Set())

// Shared selection state for calificar mode
const seleccionCalificarCompartida = ref<string | null>(null)
provide('seleccionCalificar', seleccionCalificarCompartida)

// Registro de conteo previo de hijos por componente (no reactivo)
const prevChildrenCount = new Map<number, number>()

watch(
  () => props.componentes,
  (lista) => {
    const next = new Set(expandido.value)

    lista.forEach(c => {
      const id = c.id_componente!
      const currentCount = c.children?.length ?? 0
      const previousCount = prevChildrenCount.get(id) // undefined = carga inicial

      // Expandir solo si: es la carga inicial y ya tiene hijos
      // O si acaba de recibir su primer hijo (0 → 1+)
      if (currentCount > 0 && (previousCount === undefined || previousCount === 0)) {
        next.add(id)
      }

      prevChildrenCount.set(id, currentCount)
    })

    expandido.value = next
  },
  { deep: true, immediate: true }
)

function toggleExpandido(id: number) {
  const next = new Set(expandido.value)
  if (next.has(id)) next.delete(id)
  else next.add(id)
  expandido.value = next
}

function onModificar(comp: Componente) {
  componenteSeleccionado.value = null
  emit('modificar', comp)
}

function onAgregarHijo(payload: { padre: Componente; nivel: number }) {
  componenteSeleccionado.value = null
  if (payload.padre?.id_componente) {
    const next = new Set(expandido.value)
    next.add(payload.padre.id_componente)
    expandido.value = next
  }
  emit('agregarHijo', payload)
}

function onAgregarInstrumento(indicador?: Indicador) {
  emit('agregarInstrumento', indicador)
}

function onModificarIndicador(indicador: Indicador) {
  emit('modificarIndicador', indicador)
}

function onEliminarIndicador(indicador: Indicador) {
  emit('eliminarIndicador', indicador)
}

function onAcceder(comp: Componente) {
  emit('acceder', comp)
}
</script>

<style scoped>
.tabla-componentes {
  width: 100%;
  overflow-x: auto;
}

.tabla-modern {
  width: 100%;
  border-collapse: separate;
  border-spacing: 0 6px;
  font-family: var(--font-family);
  font-size: 14px;
  table-layout: auto;
  word-wrap: break-word;
}


.tabla-modern th,
.tabla-modern td {
  padding: 10px 12px;
  border-bottom: 1px solid #ddd;
  text-align: left;
  vertical-align: middle;
}

.tabla-modern td {
  white-space: normal;
  word-break: break-word; 
  max-width: 300px; 
}
</style>
